import { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import { Box, Button, TextField } from '@mui/material';
import { shortDateTime } from '../../shared/dateTime';
import { ALERT_BUTTON_SX, OrderAlertCard } from './OrderAlertCard';
import { planningStatusAppearance } from './planningStatus';
import { useStatuses } from './statusDictionary';
import {
  approveUnassign,
  MIN_UNASSIGN_REASON_LENGTH,
  rejectUnassign,
  toErrorMessage,
  withdrawUnassign,
  type UnassignRequest,
} from './orderActionsApi';
import { recordScoped } from '../../shared/recordScoped';
import { useHasPermission } from '../PermissionGate';
import { useAssignOverride } from './AssignOverride';
import {
  REQUEST_KIND,
  approveLabels,
  changeRequestText,
  requestAlertTitle,
  requestKindOf,
} from './changeRequests';

/**
 * Wniosek o odpięcie pojazdu — na górze karty zlecenia (omówienie
 * 01.10.2026: „odpięcie auta od zlecenia ma wywołać alert u przełożonego …
 * ktoś musi takie odpięcie zaakceptować”).
 *
 * Kto widzi co (liczy API — `apps/api/src/user-offers/unassign-requests.ts`):
 * - przełożony (rola z prawem do krawędzi odpięcia) — „Akceptuję” i
 *   „Odrzucam”; odrzucenie wymaga powodu, akceptacja nie musi;
 * - wnioskujący — „Wycofaj wniosek”;
 * - pozostali — sam wniosek, bez przycisków.
 *
 * Pojazd zostaje przy zleceniu, dopóki przełożony nie zaakceptuje. Nic nie
 * wykonuje się samo — bez karencji (decyzja użytkownika 06.10.2026): wniosek
 * czeka na decyzję człowieka.
 *
 * Od 21.10.2026 (makieta back office v2, B6) ten sam baner pokazuje wnioski
 * po wyjeździe auta: o przekazanie do Trade i o zmianę pojazdu (rodzaj
 * `kind`). Akceptacja zmiany pojazdu to obsadzenie innym autem — przy
 * blokadzie z uprawnień Team Leader dostaje okno „Obsadź mimo blokady”, jak
 * przy „Zmień pojazd”.
 */

/** Dymek „Skąd ten alert?” przy wnioskach po wyjeździe auta (21.10.2026). */
export const DEPARTED_REQUEST_ORIGIN =
  'Po wyjeździe pojazdu na załadunek zmiany zatwierdza Team Leader: planista nie przekazuje zlecenia do Trade ani nie zmienia pojazdu sam, ' +
  'tylko składa wniosek z powodem.';

/** Dymek „Skąd ten alert?”. */
export const UNASSIGN_ALERT_ORIGIN =
  'Planista nie może sam odpiąć pojazdu od obsadzonego zlecenia — o oddaniu zlecenia ' +
  'do wspólnej puli decyduje przełożony. Wniosek powstaje, gdy planista poprosi ' +
  'o odpięcie z powodem. Zmiana pojazdu w ramach własnej floty nie wymaga zgody.';

/** Wniosek z rekordu karty; `null`, gdy nic nie czeka albo pole ma zły kształt. */
export function unassignRequestOf(
  record: Record<string, unknown> | undefined,
): UnassignRequest | null {
  const r = record?.unassignRequest;
  if (!r || typeof r !== 'object') return null;
  const request = r as UnassignRequest;
  return typeof request.id === 'number' ? request : null;
}

/** Wyraźna linia alertu: kto prosi, o jakie auto i dokąd wraca zlecenie. */
export function unassignRequestText(request: UnassignRequest, targetLabel: string): string {
  const vehicle = request.registrationNumber ? ` ${request.registrationNumber}` : '';
  if (request.mine) {
    return `Twój wniosek o odpięcie pojazdu${vehicle} czeka na decyzję przełożonego. Zlecenie wróci do stanu „${targetLabel}”.`;
  }
  const who = request.requestedBy?.username ?? 'Planista';
  return `${who} prosi o odpięcie pojazdu${vehicle}. Zlecenie wróci do stanu „${targetLabel}”.`;
}

/** Szara linia: kiedy złożony i dlaczego. Godzina w czasie polskim. */
export function unassignRequestDetail(request: UnassignRequest): string {
  return `Złożony ${shortDateTime(request.createdAt)}. Powód: ${request.reason}`;
}

/** Nagłówek banera — wniosek czeka, dopóki ktoś nie zdecyduje. */
export const UNASSIGN_ALERT_TITLE = 'Wniosek o odpięcie czeka na decyzję';

/** Stopka: co się dzieje do decyzji. Bez terminu — nic nie wykona się samo. */
export const UNASSIGN_ALERT_NOTE =
  'Do decyzji przełożonego pojazd zostaje przy zleceniu. Nic nie wykona się samo.';

type Decision = 'approve' | 'reject';

const UnassignRequestAlertCard = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const states = useStatuses();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  // Sekcja planowania FTL (role działowe, 01.10.2026): bez uprawnienia
  // banera nie ma — trader i gość wniosków nie składają ani nie rozstrzygają.
  const canPlan = useHasPermission('FTL_PLANNING');
  // „Obsadź mimo blokady” przy akceptacji zmiany pojazdu (21.10.2026).
  const override = useAssignOverride();

  const request = unassignRequestOf(record);
  const id = record?.id;
  if (!canPlan || !request || id === undefined || id === null) return null;

  const kind = requestKindOf(request);
  const labels = approveLabels(kind);
  const targetLabel = planningStatusAppearance(request.toStatus, states).label;
  const done = (message: string) => {
    notify(message, { type: 'success' });
    setDecision(null);
    setReason('');
    refresh();
  };
  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await action();
      done(message);
    } catch (err) {
      // Zmiana pojazdu zatrzymana blokadą z uprawnień — Team Leader może
      // obsadzić mimo blokady, z powodem (jak „Zmień pojazd”).
      const offered =
        kind === REQUEST_KIND.VEHICLE_CHANGE &&
        decision === 'approve' &&
        override.offer(err, async (overrideReason) => {
          await approveUnassign(id, request.id, overrideReason, { overrideBlocking: true });
          done(labels.done);
        });
      if (!offered) notify(toErrorMessage(err), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const back = () => {
    setDecision(null);
    setReason('');
  };
  const reasonTooShort = reason.trim().length < MIN_UNASSIGN_REASON_LENGTH;

  let actions = null;
  if (request.canDecide && decision === null) {
    actions = (
      <>
        <Button
          variant="outlined"
          sx={ALERT_BUTTON_SX}
          disabled={busy}
          onClick={() => setDecision('reject')}
        >
          Odrzucam
        </Button>
        <Button
          variant="contained"
          disableElevation
          sx={ALERT_BUTTON_SX}
          disabled={busy}
          onClick={() => setDecision('approve')}
        >
          Akceptuję
        </Button>
      </>
    );
  } else if (request.canDecide && decision !== null) {
    actions = (
      <>
        <Button color="inherit" sx={ALERT_BUTTON_SX} disabled={busy} onClick={back}>
          Wróć
        </Button>
        <Button
          variant="contained"
          color={decision === 'approve' ? 'primary' : 'error'}
          disableElevation
          sx={ALERT_BUTTON_SX}
          disabled={busy || (decision === 'reject' && reasonTooShort)}
          onClick={() =>
            void (decision === 'approve'
              ? run(() => approveUnassign(id, request.id, reason), labels.done)
              : run(
                  () => rejectUnassign(id, request.id, reason),
                  'Wniosek odrzucony. Pojazd zostaje przy zleceniu.',
                ))
          }
        >
          {decision === 'approve' ? labels.button : 'Odrzuć wniosek'}
        </Button>
      </>
    );
  } else if (request.mine) {
    actions = (
      <Button
        variant="outlined"
        sx={ALERT_BUTTON_SX}
        disabled={busy}
        onClick={() => void run(() => withdrawUnassign(id, request.id), labels.withdrawn)}
      >
        Wycofaj wniosek
      </Button>
    );
  }

  return (
    <OrderAlertCard
      tone="warning"
      title={kind === REQUEST_KIND.UNASSIGN ? UNASSIGN_ALERT_TITLE : requestAlertTitle(kind)}
      // „Wykryto” z nagłówka nie pasuje do wniosku — chwilę złożenia mówi
      // szara linia pod treścią.
      detectedAt={[]}
      origin={kind === REQUEST_KIND.UNASSIGN ? UNASSIGN_ALERT_ORIGIN : DEPARTED_REQUEST_ORIGIN}
      lines={[
        {
          id: request.id,
          text:
            kind === REQUEST_KIND.UNASSIGN
              ? unassignRequestText(request, targetLabel)
              : changeRequestText(request),
          detail: unassignRequestDetail(request),
        },
      ]}
      note={UNASSIGN_ALERT_NOTE}
      actions={actions}
    >
      {decision !== null && (
        <Box sx={{ mt: 1.5 }}>
          <TextField
            label={decision === 'approve' ? 'Komentarz do akceptacji (opcjonalnie)' : 'Powód odrzucenia'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            size="small"
            autoFocus
            sx={{ width: '100%' }}
            helperText={
              decision === 'approve'
                ? 'Komentarz zapiszemy w historii zlecenia obok powodu wniosku.'
                : `Co najmniej ${MIN_UNASSIGN_REASON_LENGTH} znaków. Wnioskujący zobaczy powód w powiadomieniu.`
            }
          />
        </Box>
      )}
      {override.dialog}
    </OrderAlertCard>
  );
};

/** Stan decyzji należy do rekordu — inna karta zaczyna od zera (`recordScoped`). */
export const UnassignRequestAlert = recordScoped(UnassignRequestAlertCard);
